/**
 * FERP Portal - Orchestration Graph (DRR-318)
 *
 * Visualizes workflow orchestration edges as a meta-graph:
 * - SPAWNS: Parent → child workflow relationships
 * - FEEDS: Data flow between workflows
 * - PRODUCES: Workflow → entity creation
 * - GATES: Prerequisite relationships
 *
 * Uses @xyflow/react for interactive graph visualization.
 * Backend-driven: All edge data comes from workflow orchestration_edges.
 */

import React, { useMemo } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  type Node,
  type Edge,
  MarkerType,
  BackgroundVariant,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Stack,
  Paper,
  Text,
  Badge,
  Group,
  Alert,
  ThemeIcon,
} from '@mantine/core';
import {
  IconPlayerPlay,
  IconArrowRight,
  IconPackage,
  IconLock,
  IconAlertTriangle,
} from '@tabler/icons-react';

export interface OrchestrationEdgeData {
  edgeType: string;
  target: string;
  sectionRef?: string | null;
}

interface OrchestrationGraphProps {
  workflowId: string;
  workflowName?: string;
  orchestrationEdges?: OrchestrationEdgeData[];
}

const EDGE_COLORS: Record<string, string> = {
  SPAWNS: '#22c55e',
  FEEDS: '#3b82f6',
  PRODUCES: '#8b5cf6',
  GATES: '#f59e0b',
};

const EDGE_ICONS: Record<string, React.ReactNode> = {
  SPAWNS: <IconPlayerPlay size={12} />,
  FEEDS: <IconArrowRight size={12} />,
  PRODUCES: <IconPackage size={12} />,
  GATES: <IconLock size={12} />,
};

interface WorkflowNodeData extends Record<string, unknown> {
  label: string;
  nodeType: 'workflow' | 'entity';
  workflowId?: string;
  entityType?: string;
}

function WorkflowNodeComponent({ data }: { data: WorkflowNodeData }) {
  const isWorkflow = data.nodeType === 'workflow';

  return (
    <Paper
      shadow="sm"
      p="xs"
      withBorder
      style={{
        minWidth: 120,
        background: isWorkflow ? '#f0fdf4' : '#faf5ff',
        borderColor: isWorkflow ? '#22c55e' : '#8b5cf6',
        borderWidth: 2,
      }}
    >
      <Handle type="target" position={Position.Left} />
      <Stack gap={4} align="center">
        <ThemeIcon
          size="sm"
          variant="light"
          color={isWorkflow ? 'green' : 'violet'}
        >
          {isWorkflow ? <IconPlayerPlay size={12} /> : <IconPackage size={12} />}
        </ThemeIcon>
        <Text size="xs" fw={500} ta="center">
          {data.label}
        </Text>
        {data.workflowId && (
          <Badge size="xs" variant="outline" color="gray">
            {data.workflowId}
          </Badge>
        )}
        {data.entityType && (
          <Badge size="xs" variant="outline" color="violet">
            {data.entityType}
          </Badge>
        )}
      </Stack>
      <Handle type="source" position={Position.Right} />
    </Paper>
  );
}

const nodeTypes = {
  workflowNode: WorkflowNodeComponent,
};

export function OrchestrationGraph({
  workflowId,
  workflowName,
  orchestrationEdges = [],
}: OrchestrationGraphProps): React.ReactElement {
  const { nodes, edges } = useMemo(() => {
    if (orchestrationEdges.length === 0) {
      return { nodes: [] as Node[], edges: [] as Edge[] };
    }

    const nodeMap = new Map<string, Node>();
    const flowEdges: Edge[] = [];

    const sourceId = workflowId;
    nodeMap.set(sourceId, {
      id: sourceId,
      type: 'workflowNode',
      position: { x: 50, y: 150 },
      data: {
        label: workflowName ?? workflowId,
        nodeType: 'workflow',
        workflowId: sourceId,
      } as WorkflowNodeData,
    });

    orchestrationEdges.forEach((edge, idx) => {
      const targetId = edge.target;
      const isEntity = edge.edgeType === 'PRODUCES';

      if (!nodeMap.has(targetId)) {
        const xOffset = isEntity ? 300 : 350;
        const yOffset = isEntity ? 50 + idx * 80 : 100 + idx * 100;

        nodeMap.set(targetId, {
          id: targetId,
          type: 'workflowNode',
          position: { x: xOffset, y: yOffset },
          data: {
            label: targetId.replace(/^(wf-|Fhir|Erp)/, ''),
            nodeType: isEntity ? 'entity' : 'workflow',
            workflowId: isEntity ? undefined : targetId,
            entityType: isEntity ? targetId : undefined,
          } as WorkflowNodeData,
        });
      }

      const edgeColor = EDGE_COLORS[edge.edgeType] ?? '#6b7280';
      flowEdges.push({
        id: `${sourceId}-${edge.edgeType}-${targetId}`,
        source: sourceId,
        target: targetId,
        label: edge.edgeType,
        labelStyle: { fontSize: 10, fill: edgeColor },
        style: { stroke: edgeColor, strokeWidth: 2 },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: edgeColor,
        },
        animated: edge.edgeType === 'FEEDS',
      });
    });

    return {
      nodes: Array.from(nodeMap.values()),
      edges: flowEdges,
    };
  }, [orchestrationEdges, workflowId, workflowName]);

  if (orchestrationEdges.length === 0) {
    return (
      <Alert color="gray" title="No Orchestration Edges" icon={<IconAlertTriangle />}>
        This workflow has no declared relationships to other workflows or entities.
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Group gap="xs">
        <Text fw={600}>Workflow Orchestration</Text>
        <Badge variant="light" color="gray" size="sm">
          {orchestrationEdges.length} edges
        </Badge>
      </Group>

      <Group gap="md">
        {Object.entries(EDGE_COLORS).map(([type, color]) => (
          <Group key={type} gap={4}>
            <ThemeIcon size="xs" variant="light" style={{ background: color, color: 'white' }}>
              {EDGE_ICONS[type]}
            </ThemeIcon>
            <Text size="xs">{type}</Text>
          </Group>
        ))}
      </Group>

      <Paper withBorder style={{ height: 400 }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          minZoom={0.5}
          maxZoom={2}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
          <Controls />
          <MiniMap
            nodeColor={(node) =>
              (node.data as WorkflowNodeData).nodeType === 'workflow' ? '#22c55e' : '#8b5cf6'
            }
          />
        </ReactFlow>
      </Paper>
    </Stack>
  );
}

export default OrchestrationGraph;
